import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import ModalManager, { ModalCallbacks } from "../components/QuotationBuilder/ModalManager";
import PreviewPDF from "../components/QuotationBuilder/PreviewPDF";
import { TripInfo, Hotel, ItineraryItem } from "../types/Quotation";
import { useItinerary } from "../hooks/useItinerary";
import { getDatePlusDays } from "../utils/date";
import { fetchHotels } from "../api/Hotels";
import { loadCityLocations } from "../api/Location";
import policies from "../data/policies.json";
import PackageTripForm from "../components/PackageBuilder/PackageTripForm";
import PackageRightSidebar from "../components/PackageBuilder/PackageRightSidebar";
import { Row, Label, Input, Section, TagInput } from "../components/PackageBuilder/UIPrimitives";
import "../css/QuotationBuilder.css";
import "../css/CreatePackagePage.css";
import { createPackage, updatePackage, fetchPackageById } from "../api/Packages";
import { TravelPackage } from "../types/TravelPackage";
// Draft bookings API (use drafts instead of confirmed bookings)
import { createBookingDraft, updateBookingDraft, fetchBookingDrafts } from "../api/BookingDraft";
import { uploadQuotation } from "../api/Quotation";
import { generatePdfBlob } from "../utils/pdf";
import { toast } from "react-toastify";
import { fetchQueryById } from "../api/Query";

// const API_BASE_URL =
//   (typeof process !== "undefined" && (process as any)?.env?.REACT_APP_API_BASE_URL) ||
//   "";

const WEB_BASE_URL =
  (typeof process !== "undefined" && (process as any)?.env?.REACT_APP_WEB_BASE_URL) ||
  "https://www.gocompasia.com";

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}
function buildAutoSlug(title: string, nights: number, places: string[]) {
  if (!title.trim()) return "";
  const days = Math.max(1, nights + 1);
  const placesPart = (places || []).filter(Boolean).slice(0, 3).join("-");
  const durationPart = `${days}d-${nights}n`;
  const slugParts = [title, durationPart];
  if (placesPart) slugParts.push(placesPart);
  return slugify(slugParts.filter(Boolean).join(" "));
}

const CreatePackagePage: React.FC = () => {
  const location = useLocation() as any;
  const packageToCopy: TravelPackage | undefined = location?.state?.packageToCopy;
  const packageToEdit: TravelPackage | undefined = location?.state?.packageToEdit;
  const packageFromDraft: any | undefined = location?.state?.packageFromDraft;
  const builderMode: 'package' | 'quotation' = (location?.state?.mode === 'quotation') ? 'quotation' : 'package';
  const sourceQueryId: number | undefined = location?.state?.queryId;

  const [tripInfo, setTripInfo] = useState<TripInfo>({
    title: "",
    subtitle: "",
    tripOverview: "",
    from: undefined,
    places: [],
    startDate: "",
    travellers: { adults: 1, children: 0, infants: 0 },
    inclusions: [],
    totalPrice: 0,
  });
  const nightsCount = tripInfo.places.reduce((sum, p) => sum + (p.nights || 0), 0);
  const [itinerary, setItinerary] = useItinerary(nightsCount);
  const [pendingItinerary, setPendingItinerary] = useState<ItineraryItem[] | null>(null);
  const [pendingApply, setPendingApply] = useState(false);

  const [heroImageFile, setHeroImageFile] = useState<File | null>(null);
  const [heroImagePreview, setHeroImagePreview] = useState<string>("");
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [pricePer, setPricePer] = useState<"person" | "package" | "night">("person");
  const [currency, setCurrency] = useState<"INR" | "USD">("INR");
  const [isPublished, setIsPublished] = useState(false);
  const [rating, setRating] = useState("");
  const [strikePrice, setStrikePrice] = useState<number>(0);
  const [highlights, setHighlights] = useState<string[]>([]);
  const [features, setFeatures] = useState<string[]>([]); // not sent to backend
  const [goodToKnow, setGoodToKnow] = useState<string[]>([]);
  const [exclusions, setExclusions] = useState<string[]>([]);
  const [addons, setAddons] = useState<Array<{ name: string; price: number; currency: string; description: string }>>([]);
  const [reviews, setReviews] = useState<Array<{ date: string; user: string; rating: number; comment: string }>>([]);
  const [faq, setFaq] = useState<Array<{ question: string; answer: string }>>([]);
  const [prefilled, setPrefilled] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [queryCustomerId, setQueryCustomerId] = useState<number | undefined>(undefined);

  const [hotelCache, setHotelCache] = useState<{ data: any[]; loading: boolean; error: any | null }[]>([]);
  const [hotelImageFiles, setHotelImageFiles] = useState<Record<string, File | null>>({});
  const handleHotelImageSelect = (dayIndex: number, hotelIndex: number, file: File | null) => {
    const key = `${dayIndex}-${hotelIndex}`;
    setHotelImageFiles((prev) => ({ ...prev, [key]: file }));
  };

  // Off-screen PDF preview container to generate/share PDF
  const pdfPreviewRef = useRef<HTMLDivElement | null>(null);

  // Load customerId for the source query when in quotation mode
  useEffect(() => {
    (async () => {
      try {
        if (builderMode === 'quotation' && sourceQueryId) {
          const q = await fetchQueryById(sourceQueryId);
          const cid = (q as any)?.customerId || (q as any)?.customer?.id;
          if (cid) setQueryCustomerId(Number(cid));
        }
      } catch {
        // ignore
      }
    })();
  }, [builderMode, sourceQueryId]);

  useEffect(() => {
    if (
      tripInfo.from?.value &&
      tripInfo.places.length > 0 &&
      tripInfo.startDate
    ) {
      const initial = tripInfo.places.map(() => ({ data: [], loading: true, error: null }));
      setHotelCache(initial);
      tripInfo.places.forEach((place, idx) => {
        const offset = tripInfo.places.slice(0, idx).reduce((s, p) => s + (p.nights || 0), 0);
        const ci = getDatePlusDays(tripInfo.startDate, offset);
        const co = getDatePlusDays(ci, place.nights || 0);
        (async () => {
          try {
            let cityCode = (place.value || '').trim();
            const isAirport = /^[A-Za-z]{3}$/.test(cityCode);
            const isCityId = /^\d+$/.test(cityCode);
            if (!isAirport && !isCityId) {
              const opts = await loadCityLocations(place.label || '');
              const match = (opts || []).find(o => o.type === 'city' && o.label?.toLowerCase().includes((place.label || '').toLowerCase()));
              if (match) cityCode = match.value;
            }
            // Guard: only call API when city code and dates are valid
            const validCity = /^(?:[A-Za-z]{3}|\d+)$/.test(cityCode);
            const ciDate = new Date(ci);
            const coDate = new Date(co);
            if (!validCity || isNaN(ciDate.getTime()) || isNaN(coDate.getTime()) || ciDate >= coDate) {
              setHotelCache((prev) => {
                const next = [...prev];
                next[idx] = { data: [], loading: false, error: new Error('City or dates incomplete/invalid for hotel search') };
                return next;
              });
              return;
            }

            const resp = await fetchHotels({
              city: cityCode,
              checkIn: ci,
              checkOut: co,
              rooms: [{ Adults: tripInfo.travellers.adults, Children: tripInfo.travellers.children, ChildrenAges: [] }],
              page: 1,
            });
            const hotels: Hotel[] = (resp && (resp as any).items) || [];
            setHotelCache((prev) => {
              const next = [...prev];
              next[idx] = { data: hotels, loading: false, error: null };
              return next;
            });
          } catch (err: any) {
            setHotelCache((prev) => {
              const next = [...prev];
              next[idx] = { data: [], loading: false, error: err };
              return next;
            });
          }
        })();
      });
    }
  }, [tripInfo.from, tripInfo.places, tripInfo.startDate, tripInfo.travellers]);

  // Helpers to sanitize API arrays that may contain stringified braces
  function coerceStringArray(arr: any): string[] {
    if (!Array.isArray(arr)) return [];
    const out: string[] = [];
    arr.forEach((v) => {
      if (typeof v === 'string') {
        const s = v.trim();
        // Strip surrounding braces and quotes if present {"a","b"}
        const inner = s.replace(/[{}]/g, '').replace(/"/g, '"');
        if (inner.includes(',')) {
          inner.split(',').forEach(p => { const t = p.replace(/"/g, '"').replace(/"/g, '').trim(); if (t) out.push(t); });
        } else if (inner) {
          const t = inner.replace(/"/g, '').trim(); if (t) out.push(t);
        }
      }
    });
    return out.filter(Boolean);
  }

  function coerceCities(cities: any): string[] {
    if (Array.isArray(cities) && cities.length > 0) {
      const raw = cities.map((c: any) => (typeof c === 'string' ? c : String(c)));
      const vals = raw.map(s => s.replace(/[{}]/g, '').replace(/"/g, '').replace(/"/g, '').trim()).filter(Boolean);
      return vals.length ? vals : raw;
    }
    return [];
  }

  const prefillFromApi = useCallback((p: any) => {
    const durationNights = Number(p.duration_nights || p.durationNights || 0) || 0;
    // const durationDays = Number(p.duration_days || p.durationDays || (durationNights + 1)) || (durationNights + 1);
    const apiCities = coerceCities(p.cities || []);
    const primaryCity = apiCities[0] || p.title || '';

    // Build places from cities and nights
    let places: any[] = [];
    if (apiCities.length > 0) {
      const baseNights = Math.floor(durationNights / apiCities.length);
      const extra = durationNights % apiCities.length;
      places = apiCities.map((c, idx) => ({ label: c, value: c, nights: baseNights + (idx < extra ? 1 : 0) }));
    } else {
      places = [{ label: primaryCity, value: primaryCity, nights: durationNights }];
    }

    const priceInfo = p.price || {};
    const per = priceInfo.per;
    let unit: typeof pricePer = 'person';
    if (per === 'person' || per === 'per_person') unit = 'person';
    else if (per === 'total' || per === 'per_couple' || per === 'package') unit = 'package';
    else if (per === 'night') unit = 'night';

    setTripInfo(prev => ({
      ...prev,
      title: p.title || '',
      subtitle: p.subtitle || '',
      places,
      startDate: (p.itinerary && p.itinerary[0]?.date) || prev.startDate,
      inclusions: coerceStringArray(p.inclusions),
      totalPrice: Number(priceInfo.base || 0) || 0,
    }));
    setPricePer(unit);
    setCurrency((priceInfo.currency === 'USD' || priceInfo.currency === 'INR') ? priceInfo.currency : 'INR');
    setRating(p.rating || '');
    setStrikePrice(Number(priceInfo.strike || 0) || 0);
    setHighlights(coerceStringArray(p.highlights));
    setGoodToKnow(coerceStringArray(p.good_to_know || p.goodToKnow));
    setExclusions(coerceStringArray(p.exclusions));
    setAddons(Array.isArray(p.addons) ? p.addons : []);
    setReviews(Array.isArray(p.reviews) ? p.reviews : []);
    setFaq(Array.isArray(p.faq) ? p.faq : []);
    setIsPublished(!!(p.is_published || p.isPublished));
    if (packageToEdit && p.id) setEditingId(String(p.id));

    // Build itinerary days from API
    const targetLen = Math.max(1, durationNights + 1);
    const cityLabel = primaryCity;
    const byDay = (arr: any[], key = 'day') => {
      const m = new Map<number, any[]>();
      (Array.isArray(arr) ? arr : []).forEach(it => { const d = Number(it[key] || 0); if (!m.has(d)) m.set(d, []); m.get(d)!.push(it); });
      return m;
    };
    const itinDays = Array.isArray(p.itinerary) ? p.itinerary : [];
    const flightsByDay = byDay(p.flights || [], 'day');
    const activitiesByDay = byDay(p.activities || [], 'day');
    const transfersByDay = byDay(p.transfers || [], 'day');
    const hotelsByDay = new Map<number, any[]>();
    if (itinDays.length) {
      itinDays.forEach((d: any) => { if (Array.isArray(d.hotels)) hotelsByDay.set(Number(d.day || 0), d.hotels); });
    } else if (Array.isArray(p.hotels) && p.hotels.length) {
      hotelsByDay.set(1, p.hotels);
    }

    const buildFlight = (f: any) => ({
      provider: f.provider || 'API',
      airline: f.airline || '',
      flightNumber: f.flightNumber || f.number || '',
      from: f.from || '',
      to: f.to || '',
      departureTime: f.departureTime || f.boardingDateTime || (f.date ? `${f.date}T00:00:00` : ''),
      arrivalTime: f.arrivalTime || f.landingDateTime || (f.date ? `${f.date}T00:00:00` : ''),
      duration: f.duration || '',
      stops: Number(f.stops || 0),
      price: Number(f.price || 0),
      class: f.class || '',
    });
    const buildHotel = (h: any) => ({
      name: h.name || '',
      starRating: String(h.rating || h.starRating || '0'),
      checkin_time: (h as any).checkin_time || '',
      checkout_time: (h as any).checkout_time || '',
      thumbnail: h.thumbnail || h.image || '',
    });

    const buildActivity = (a: any) => ({
      id: a.id,
      city: a.city,
      name: a.name,
      description: a.description,
      duration_minutes: a.duration_minutes,
      start_time: a.start_time,
      remarks: a.remarks,
      rules: a.rules,
      picture_url: a.picture_url,
      category: a.category,
      currency: a.currency,
      from_price_paise: a.from_price_paise,
      variant_count: a.variant_count,
      variants: a.variants,
    });

    const newItin: ItineraryItem[] = Array.from({ length: targetLen }).map((_, i) => {
      const dayNo = i + 1;
      const dIt = itinDays.find((d: any) => Number(d.day || 0) === dayNo) || {};
      const flights = (flightsByDay.get(dayNo) || []).map(buildFlight);
      const activities = (activitiesByDay.get(dayNo) || []).map(buildActivity);
      const transfers = (transfersByDay.get(dayNo) || []).map((t: any) => ({
        provider: t.provider,
        name: t.name,
        vehicle: t.vehicle,
        price: Number(t.price || 0),
        currency: t.currency || 'INR',
        pickupDate: t.pickupDate || t.date,
        pickupTime: t.pickupTime || t.time,
        pickupFromAirport: t.pickupFromAirport,
        cityId: t.cityId,
        airportCode: t.airportCode,
        hotelCode: t.hotelCode,
        meta: t.meta,
      }));
      const hotels = (hotelsByDay.get(dayNo) || []).map((h: any) => {
        const base = buildHotel(h);
        const checkInDate = dIt.date || (p.itinerary && p.itinerary[i]?.date) || tripInfo.startDate || undefined;
        // checkout: next day's date if available
        const nextDate = (p.itinerary && p.itinerary[i+1]?.date) || undefined;
        return { ...base, checkInDate, checkOutDate: nextDate } as any;
      });
      const placeLabel = dIt.place?.label || dIt.city || cityLabel;
      return {
        day: dayNo,
        title: dIt.title || '',
        place: { label: placeLabel, value: placeLabel, nights: 1 },
        flights,
        activities,
        hotels,
        transfers,
      } as any;
    });
    // Defer applying itinerary until nights-based auto-reset has occurred
    setPendingItinerary(newItin);
    setPendingApply(true);
  }, [tripInfo.startDate, packageToEdit]);

  // After nightsCount recalculates from tripInfo.places, apply pending itinerary once
  useEffect(() => {
    if (!pendingApply || !pendingItinerary) return;
    const expected = pendingItinerary.length - 1;
    if (expected === nightsCount) {
      setItinerary(pendingItinerary);
      setPendingApply(false);
      setPendingItinerary(null);
    }
  }, [pendingApply, pendingItinerary, nightsCount, setItinerary]);

  // Prefill when navigating with a package to edit or copy — fetch full details by id
  useEffect(() => {
    if (prefilled) return;
    const p = packageFromDraft || packageToEdit || packageToCopy;
    if (!p) return;
    (async () => {
      try {
        if (packageFromDraft) {
          prefillFromApi(packageFromDraft);
        } else {
          const id = (p as any).id ? String((p as any).id) : undefined;
          const full = id ? await fetchPackageById(id) : p;
          prefillFromApi(full);
        }
      } finally {
        setPrefilled(true);
      }
    })();
  }, [packageFromDraft, packageToCopy, packageToEdit, prefilled, prefillFromApi]);

  const handleTripInfoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | { name: string; value: any }) => {
      const name = "target" in e ? e.target.name : e.name;
      let value = "target" in e ? e.target.value : e.value;
      if (name === "totalPrice") value = Number(value);
      setTripInfo((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const cities = useMemo(() => tripInfo.places.map((p) => p.label || p.value).filter(Boolean), [tripInfo.places]);
  const slug = useMemo(() => buildAutoSlug(tripInfo.title, nightsCount, cities), [tripInfo.title, nightsCount, cities]);
  const websitePreviewUrl = useMemo(() => (slug ? `${WEB_BASE_URL}/packages/${encodeURIComponent(slug)}` : ""), [slug]);

  // Reverse-control inclusions based on actual selections
  useEffect(() => {
    const hasFlights = itinerary.some((d) => (d.flights || []).length > 0);
    const hasHotels = itinerary.some((d) => (d.hotels || []).length > 0);
    const hasActivities = itinerary.some((d) => (d.activities || []).length > 0);
    const hasTransfers = itinerary.some((d) => (d.transfers || []).length > 0);
    setTripInfo((prev) => {
      const next = new Set(prev.inclusions);
      if (hasFlights) next.add('flight'); else next.delete('flight');
      if (hasHotels) next.add('hotel'); else next.delete('hotel');
      if (hasActivities) next.add('activity'); else next.delete('activity');
      if (hasTransfers) next.add('transfer'); else next.delete('transfer');
      const nextArr = Array.from(next);
      if (nextArr.length === prev.inclusions.length && nextArr.every((x) => prev.inclusions.includes(x))) return prev;
      return { ...prev, inclusions: nextArr };
    });
  }, [itinerary]);

  function buildPackagePayload() {
    const durationNights = nightsCount;
    const durationDays = Math.max(1, durationNights + 1);
      // Build hotels array (omit image here; images sent as files)
      const hotelsData = itinerary.flatMap((d) =>
        (d.hotels || []).map((h) => ({
          city: d.place?.label || d.place?.value || '',
          name: h.name,
          rating: Number(h.starRating || '0') || 0,
          amenities: Array.isArray(h.amenities) ? h.amenities : [],
        }))
      );

      // Flattened lists for API: flights, activities, transfers
      const flightsData = itinerary.flatMap((d, di) =>
        (d.flights || [])
          .slice()
          .sort((a, b) => {
            const ta = new Date(a.departureTime || '').getTime();
            const tb = new Date(b.departureTime || '').getTime();
            return (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
          })
          .map((f) => ({
            day: di + 1,
            date: getDatePlusDays(tripInfo.startDate, di),
            airline: f.airline,
            flightNumber: f.flightNumber,
            from: f.from,
            to: f.to,
            departureTime: f.departureTime,
            arrivalTime: f.arrivalTime,
            duration: f.duration,
            stops: f.stops,
            price: f.price,
            class: f.class,
            provider: f.provider,
            name: f.name,
            aircraft: f.aircraft,
            boardingDateTime: f.boardingDateTime,
            landingDateTime: f.landingDateTime,
            fromAirportName: f.fromAirportName,
            toAirportName: f.toAirportName,
            terminal: f.terminal,
            baggage: f.baggage,
          }))
      );

      const activitiesData = itinerary.flatMap((d, di) =>
        (d.activities || [])
          .slice()
          .sort((a, b) => {
            const ta = a.start_time ? new Date(a.start_time).getTime() : 0;
            const tb = b.start_time ? new Date(b.start_time).getTime() : 0;
            return ta - tb;
          })
          .map((a) => ({
            day: di + 1,
            date: getDatePlusDays(tripInfo.startDate, di),
            id: a.id,
            slug: a.slug,
            name: a.name,
            description: a.description,
            duration_minutes: a.duration_minutes,
            start_time: a.start_time,
            remarks: a.remarks,
            rules: a.rules,
            picture_url: a.picture_url,
            city: a.city,
            city_code: a.city_code,
            category: a.category,
            currency: a.currency,
            from_price_paise: a.from_price_paise,
            variant_count: a.variant_count,
            variants: a.variants,
          }))
      );

      const transfersData = itinerary.flatMap((d, di) =>
        (d.transfers || [])
          .slice()
          .sort((a, b) => {
            const ta = new Date(`${a.pickupDate || ''}T${(a.pickupTime || '00:00')}:00`).getTime();
            const tb = new Date(`${b.pickupDate || ''}T${(b.pickupTime || '00:00')}:00`).getTime();
            return (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
          })
          .map((t) => ({
            day: di + 1,
            date: getDatePlusDays(tripInfo.startDate, di),
            provider: t.provider,
            name: t.name,
            vehicle: t.vehicle,
            price: t.price,
            currency: t.currency,
            pickupDate: t.pickupDate,
            pickupTime: t.pickupTime,
            pickupFromAirport: t.pickupFromAirport,
            cityId: t.cityId,
            airportCode: t.airportCode,
            hotelCode: t.hotelCode,
            meta: t.meta,
          }))
      );

      // Helper: map a day index (0-based) to place index based on nights allocation
      const placeBoundaries = tripInfo.places.reduce<{ start: number; end: number; idx: number }[]>((acc, p, idx) => {
        const start = acc.length === 0 ? 0 : acc[acc.length - 1].end;
        const end = start + Math.max(0, p.nights || 0);
        acc.push({ start, end, idx });
        return acc;
      }, []);
      const getPlaceIndexForDay = (dayIdx: number): number | null => {
        const entry = placeBoundaries.find(b => dayIdx >= b.start && dayIdx < b.end);
        return entry ? entry.idx : null;
      };

      // Day-wise itinerary with hotels, flights, activities, transfers
      // Track hotel check-in/checkout messages so they are added only once per hotel per place
      const seenHotelCheckins = new Set<string>();
      const seenHotelCheckouts = new Set<string>();
      const itineraryDays = itinerary.map((d, di) => {
        const dayDate = getDatePlusDays(tripInfo.startDate, di);
        // Sort flights by time
        const flights = (d.flights || []).slice().sort((a, b) => {
          const ta = new Date(a.departureTime || '').getTime();
          const tb = new Date(b.departureTime || '').getTime();
          return (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
        });
        // Sort activities by start time
        const activities = (d.activities || []).slice().sort((a, b) => {
          const ta = a.start_time ? new Date(a.start_time).getTime() : 0;
          const tb = b.start_time ? new Date(b.start_time).getTime() : 0;
          return ta - tb;
        });
        // Sort transfers by pickup time
        const transfers = (d.transfers || []).slice().sort((a, b) => {
          const ta = new Date(`${a.pickupDate || ''}T${(a.pickupTime || '00:00')}:00`).getTime();
          const tb = new Date(`${b.pickupDate || ''}T${(b.pickupTime || '00:00')}:00`).getTime();
          return (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
        });
        // Hotels are selected per place; attach that place's hotels for each day within its range
        const placeIdx = getPlaceIndexForDay(di);
        const hotelsSource = placeIdx !== null ? (itinerary[placeIdx]?.hotels || []) : [];
        // Do not include the full roomVariants array; only keep the selected roomVariant
        const hotels = hotelsSource.map(h => {
          const { roomVariants, ...sanitized } = (h as any) || {};
          return { ...sanitized, day: di + 1, date: dayDate };
        });

        // Derive human-readable points for the day from Flights, Hotels, Transfers, Activities
        const points: string[] = [];
        const dateOnly = (s?: string) => (typeof s === 'string' && s.length >= 10 ? s.slice(0, 10) : undefined);
        

        // Flights: arrival and departure entries when they fall on this day
        flights.forEach((f) => {
          const depDate = dateOnly(f.departureTime);
          const arrDate = dateOnly(f.arrivalTime);
          if (depDate === dayDate && f.from) {
            points.push(`Depart from ${f.from}`);
          }
          if (arrDate === dayDate && f.to) {
            points.push(`Arrival at ${f.to}`);
          }
        });

        // Hotels: check-in on the first day in the place, checkout on the last day in the place
        if (placeIdx !== null) {
          const boundary = placeBoundaries[placeIdx];
          const isFirstDayInPlace = di === boundary.start;
          const isLastDayInPlace = di === Math.max(boundary.end - 1, boundary.start);
          // Deduplicate hotels by id/name for point generation
          const uniqueHotels = hotels.reduce((acc: any[], h: any) => {
            const key = (h.hotelId || h.name || '').toString();
            if (!key) return acc.concat([h]);
            if (!acc.some(x => (x.hotelId || x.name || '').toString() === key)) acc.push(h);
            return acc;
          }, [] as any[]);
          uniqueHotels.forEach((h: any) => {
            const ci = (h.checkInDate || (isFirstDayInPlace ? dayDate : undefined)) as string | undefined;
            const co = (h.checkOutDate || (isLastDayInPlace ? getDatePlusDays(tripInfo.startDate, di + 1) : undefined)) as string | undefined;
            const hotelKey = `${placeIdx}|${h.hotelId || h.name || ''}`;
            if (isFirstDayInPlace && h.name && ci && !seenHotelCheckins.has(hotelKey)) {
              points.push(`Check-in at ${h.name}`);
              seenHotelCheckins.add(hotelKey);
            }
            if (isLastDayInPlace && h.name && co && !seenHotelCheckouts.has(hotelKey)) {
              points.push(`Checkout from ${h.name}`);
              seenHotelCheckouts.add(hotelKey);
            }
          });
        }

        // Transfers: list any transfers for the day
        transfers.forEach((t: any) => {
          const when = t.pickupTime ? ` at ${t.pickupTime}` : '';
          const label = t.name || t.vehicle || 'Transfer';
          points.push(`Transfer: ${label}${when}`);
        });

        // Activities: add activity titles
        activities.forEach((a: any) => {
          const title = a?.name || a?.title;
          if (title) points.push(title);
        });

        const uniquePoints = Array.from(new Set(points.filter(Boolean)));
        return {
          day: di + 1,
          title: d.title || '',
          date: dayDate,
          hotels,
          flights,
          activities,
          transfers,
          points: uniquePoints,
        };
      });

      // Build cities with days information
      const citiesWithDays = tripInfo.places.map(place => ({
        name: place.label || place.value || '',
        days: place.nights || 0
      }));

      return {
      title: tripInfo.title,
      subtitle: tripInfo.subtitle || undefined,
      // hero_image and gallery are uploaded as files; backend sets URLs
      rating: rating || undefined,
      cities: citiesWithDays,
      durationDays: durationDays,
      durationNights: durationNights,
      fixedDepartureDate: tripInfo.startDate,
      price: {
        base: Number(tripInfo.totalPrice || 0),
        strike: strikePrice > 0 ? strikePrice : undefined,
        per: pricePer,
        currency,
      },
      highlights: [...highlights],
      goodToKnow: goodToKnow,
      itinerary: itineraryDays,
      flights: flightsData,
      activities: activitiesData,
      transfers: transfersData,
      hotels: hotelsData,
      inclusions: [...tripInfo.inclusions],
      exclusions,
      addons,
      reviews,
      policies,
      faq,
      seo: {},
      isPublished: isPublished,
    };
  }

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  async function savePackage() {
    setSaving(true);
    setError("");
    try {
      if (!tripInfo.title) throw new Error("Trip title is required");
      if (!slug) {
        // Slug is auto-generated on backend; keep local slug for preview only
      }
      const payload = buildPackagePayload();
      const formData = new FormData();

      // Simple fields
      formData.append("title", payload.title);
      if (payload.subtitle) formData.append("subtitle", payload.subtitle);
      if (payload.rating) formData.append("rating", String(payload.rating));
      formData.append("durationDays", String(payload.durationDays));
      formData.append("durationNights", String(payload.durationNights));
      formData.append("isPublished", String(payload.isPublished));
      if (payload.fixedDepartureDate) formData.append("fixedDepartureDate", payload.fixedDepartureDate);

      // Complex fields as JSON strings
      formData.append("cities", JSON.stringify(payload.cities || []));
      formData.append("price", JSON.stringify(payload.price));
      formData.append("highlights", JSON.stringify(payload.highlights || []));
      formData.append("goodToKnow", JSON.stringify(payload.goodToKnow || []));
      formData.append("itinerary", JSON.stringify(payload.itinerary || []));
      formData.append("flights", JSON.stringify(payload.flights || []));
      formData.append("activities", JSON.stringify(payload.activities || []));
      formData.append("transfers", JSON.stringify(payload.transfers || []));
      formData.append("hotels", JSON.stringify(payload.hotels || []));
      formData.append("inclusions", JSON.stringify(payload.inclusions || []));
      formData.append("exclusions", JSON.stringify(payload.exclusions || []));
      formData.append("addons", JSON.stringify(payload.addons || []));
      formData.append("reviews", JSON.stringify(payload.reviews || []));
      formData.append("policies", JSON.stringify(payload.policies || {}));
      formData.append("faq", JSON.stringify(payload.faq || []));
      formData.append("seo", JSON.stringify(payload.seo || {}));

      // Files
      if (heroImageFile) formData.append("hero_image", heroImageFile);
      if (galleryFiles && galleryFiles.length > 0) galleryFiles.forEach((f) => formData.append("gallery", f));

      // Optional hotel images aligned by index
      const flatHotels = itinerary.flatMap((d, di) => (d.hotels || []).map((_, hi) => ({ di, hi })));
      flatHotels.forEach(({ di, hi }, idx) => {
        const key = `${di}-${hi}`;
        const file = hotelImageFiles[key];
        if (file) formData.append(`hotels[${idx}][image]`, file);
      });

      if (editingId) {
        await updatePackage(editingId, formData);
      } else {
        await createPackage(formData);
      }
    } catch (e: any) {
      setError(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  // Handlers for file inputs with preview
  function onHeroFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setHeroImageFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setHeroImagePreview(url);
    } else {
      setHeroImagePreview("");
    }
  }

  function onGalleryFilesChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setGalleryFiles(files);
  }

  return (
    <Layout>
      {/* Hidden off-screen PDF preview root for generation */}
      <div style={{ position: 'absolute', left: -10000, top: -10000, width: 800 }} aria-hidden ref={pdfPreviewRef}>
        <PreviewPDF
          client={{ name: '', phoneNumber: '', email: '' }}
          tripInfo={tripInfo}
          itinerary={itinerary as any}
          generalRules={''}
          highlights={(highlights || []).join(', ')}
          highlightImages={[]}
          inclusionImages={[]}
          itineraryImages={[]}
        />
      </div>
      <div className="package-editor-grid">
        <ModalManager
          nightsCount={nightsCount}
          tripInfo={tripInfo}
          itinerary={itinerary}
          setItinerary={setItinerary}
          startDate={tripInfo.startDate}
          travellers={tripInfo.travellers}
          hotelCache={hotelCache}
        >
          {({ openFlightModal, openHotelModal, openActivityModal, openTransferModal, removeActivity, removeFlight, removeHotel, removeTransfer }: ModalCallbacks) => (
            <div>
              <PackageTripForm
                tripInfo={tripInfo}
                handleTripInfoChange={handleTripInfoChange}
                itinerary={itinerary}
                onChangeDayTitle={(dayIndex, title) => {
                  setItinerary(prev => prev.map((d, i) => (i === dayIndex ? { ...d, title } : d)));
                }}
                openFlightModal={openFlightModal}
                openHotelModal={openHotelModal}
                openActivityModal={openActivityModal}
                openTransferModal={openTransferModal}
                removeActivity={removeActivity}
                removeFlight={removeFlight}
                removeHotel={removeHotel}
                removeTransfer={removeTransfer}
                onHotelImageSelect={handleHotelImageSelect}
              />

              {builderMode === 'package' && (
                <Section title="Media and other information">
                  <Label>Hero image</Label>
                  <input type="file" accept="image/*" onChange={onHeroFileChange} className="package-editor-input" />

                  <div className="package-editor-mt-12">
                    <Label>Gallery images</Label>
                    <input type="file" accept="image/*" multiple onChange={onGalleryFilesChange} className="package-editor-input" />
                    {galleryFiles.length > 0 && (
                      <div className="package-editor-small-note" style={{ marginTop: 6 }}>{galleryFiles.length} image(s) selected</div>
                    )}
                  </div>

                  <Row>
                    <div>
                      <Label>Rating</Label>
                      <Input value={rating} onChange={(e) => setRating(e.target.value)} placeholder="e.g., 4.5" />
                    </div>
                  </Row>

                  <Row>
                    <div>
                      <Label>Price unit</Label>
                      <select value={pricePer} onChange={(e) => setPricePer(e.target.value as any)} className="package-editor-input">
                        <option value="person">person</option>
                        <option value="package">package</option>
                        <option value="night">night</option>
                      </select>
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <select value={currency} onChange={(e) => setCurrency(e.target.value as any)} className="package-editor-input">
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                    <div>
                      <Label>Strike price (optional)</Label>
                      <Input type="number" min={0} value={strikePrice} onChange={(e) => setStrikePrice(Number(e.target.value || 0))} placeholder="Original price for discount display" />
                    </div>
                  </Row>

                  <div className="package-editor-mt-12">
                    <Label>Highlights</Label>
                    <TagInput value={highlights} onChange={setHighlights} placeholder="Key attractions and experiences" />
                  </div>

                  <div className="package-editor-mt-12">
                    <Label>Features</Label>
                    <TagInput value={features} onChange={setFeatures} placeholder="Key features of the package" />
                  </div>

                  <div className="package-editor-mt-12">
                    <Label>Good to know</Label>
                    <TagInput value={goodToKnow} onChange={setGoodToKnow} placeholder="Important travel information" />
                  </div>

                  <div className="package-editor-mt-12">
                    <Label>Inclusions</Label>
                    <TagInput value={tripInfo.inclusions} onChange={(vals) => setTripInfo((prev) => ({ ...prev, inclusions: vals }))} placeholder="What's included" />
                  </div>

                  <div className="package-editor-mt-12">
                    <Label>Exclusions</Label>
                    <TagInput value={exclusions} onChange={setExclusions} placeholder="What's not included" />
                  </div>

                  <div className="package-editor-mt-12">
                    <Label>Add-ons (optional)</Label>
                    <div className="package-editor-small-note">Add optional services that customers can purchase</div>
                    <div className="package-editor-subsection-box">
                      <div className="package-editor-small-note">{addons.length === 0 ? "No add-ons added yet" : `${addons.length} add-on(s) configured`}</div>
                      <button type="button" className="package-editor-btn-ghost" onClick={() => setAddons([...addons, { name: "New Add-on", price: 0, currency: "INR", description: "" }])}>+ Add</button>
                    </div>
                  </div>

                  <div className="package-editor-mt-12">
                    <Label>Reviews (optional)</Label>
                    <div className="package-editor-small-note">Customer reviews and testimonials</div>
                    <div className="package-editor-subsection-box">
                      <div className="package-editor-small-note">{reviews.length === 0 ? "No reviews added yet" : `${reviews.length} review(s) configured`}</div>
                      <button type="button" className="package-editor-btn-ghost" onClick={() => setReviews([...reviews, { date: new Date().toISOString().split('T')[0], user: "Customer", rating: 5, comment: "" }])}>+ Add</button>
                    </div>
                  </div>

                  <div className="package-editor-mt-12">
                    <Label>FAQ (optional)</Label>
                    <div className="package-editor-small-note">Frequently asked questions</div>
                    <div className="package-editor-subsection-box">
                      <div className="package-editor-small-note">{faq.length === 0 ? "No FAQ added yet" : `${faq.length} FAQ item(s) configured`}</div>
                      <button type="button" className="package-editor-btn-ghost" onClick={() => setFaq([...faq, { question: "New Question", answer: "" }])}>+ Add</button>
                    </div>
                  </div>

                  {error && <div className="package-editor-error">{error}</div>}

                  <Row>
                    <div>
                      <Label>Publish Status</Label>
                      <div className="package-editor-checkbox-row">
                        <input type="checkbox" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
                        <span style={{ marginLeft: 8 }}>Make it live</span>
                      </div>
                    </div>
                  </Row>

                  <div className="package-editor-actions">
                    <button type="button" onClick={savePackage} disabled={saving} className="package-editor-btn-primary">{saving ? (editingId ? "Updating…" : "Saving…") : (editingId ? "Update Package" : "Save Package")}</button>
                    {!!websitePreviewUrl && (
                      <a href={websitePreviewUrl} target="_blank" rel="noreferrer" className="package-editor-btn-ghost">Preview on website ↗</a>
                    )}
                  </div>
                </Section>
              )}

              {builderMode === 'quotation' && (
                <div className="package-editor-actions" style={{ marginTop: 16 }}>
                  <ShareQuotationButton
                    nightsCount={nightsCount}
                    queryId={sourceQueryId}
                    startDate={tripInfo.startDate}
                    totalPrice={tripInfo.totalPrice}
                    providerRef={buildPackagePayload()}
                    pdfRef={pdfPreviewRef}
                    currency={currency}
                    title={tripInfo.title}
                    customerId={queryCustomerId}
                  />
                </div>
              )}
            </div>
          )}
        </ModalManager>

        <PackageRightSidebar
          heroImage={heroImagePreview}
          tripInfo={tripInfo}
          pricePer={pricePer}
          currency={currency}
          rating={rating}
          strikePrice={strikePrice}
          slug={slug}
          websitePreviewUrl={websitePreviewUrl}
          cities={cities}
          nightsCount={nightsCount}
          highlights={highlights}
          exclusions={exclusions}
          addons={addons}
          reviews={reviews}
          faq={faq}
          itinerary={itinerary}
          onChangeTotalPrice={(value) => handleTripInfoChange({ name: 'totalPrice', value })}
        />
      </div>
    </Layout>
  );
};

// Inline button component for Share Quotation to keep main component tidy
function ShareQuotationButton({ nightsCount, queryId, startDate, totalPrice, providerRef, pdfRef, currency, title, customerId }: { nightsCount: number; queryId?: number; startDate: string; totalPrice: number; providerRef: any; pdfRef: React.RefObject<HTMLDivElement | null>; currency: 'INR' | 'USD'; title: string; customerId?: number; }) {
  const [isSharing, setIsSharing] = useState(false);
  const canShare = Boolean(queryId && startDate && nightsCount >= 0 && Number(totalPrice || 0) >= 0 && !isSharing);

  const handleShare = async () => {
    if (!queryId) return;
    setIsSharing(true);
    try {
      console.log('[ShareQuotation] Begin', { queryId, nightsCount, startDate, totalPrice });
      const travelStartDate = startDate;
      const travelEndDate = getDatePlusDays(startDate, nightsCount);

      // 1) Start PDF generation in background with a timeout, don't block API calls
      const el = pdfRef.current;
      const pdfPromise: Promise<Blob | null> = (async () => {
        if (!el) return null;
        const filename = `Quotation-${queryId}.pdf`;
        console.log('[ShareQuotation] PDF generation start', { filename });
        try {
          const blob = await generatePdfBlob(el, filename);
          console.log('[ShareQuotation] PDF generation complete', { sizeBytes: (blob as any)?.size });
          return blob as Blob;
        } catch (e) {
          console.error('PDF generation failed', e);
          return null;
        }
      })();

      // 2) Upsert booking draft (not a confirmed booking) immediately
      let updated = false;
      try {
        console.log('[ShareQuotation] Draft upsert: fetch existing');
        const all = await fetchBookingDrafts(queryId);
        const existing = (all || []).find((b: any) => Number(b.query_id || b.queryId || 0) === Number(queryId));
        const amountPaise = Math.max(0, Math.round(Number(totalPrice || 0) * 100));
        if (existing) {
          console.log('[ShareQuotation] Draft upsert: updating', { id: existing.id });
          await updateBookingDraft(existing.id, {
            providerRef,
            startAt: travelStartDate || null,
            endAt: travelEndDate || null,
            amountPaise,
            currency,
            title: title || undefined,
          } as any);
          toast.success('Booking draft updated');
          console.log('[ShareQuotation] Draft upsert: update success');
          updated = true;
        } else {
          const draftPayload = {
            productType: 'package',
            provider: 'CRM',
            title: title || 'Package Quotation',
            currency,
            amountPaise,
            startAt: travelStartDate || null,
            endAt: travelEndDate || null,
            providerRef,
            queryId: queryId!,
            customerId,
          } as any;
          console.log('[ShareQuotation] Draft upsert: creating', draftPayload);
          await createBookingDraft(draftPayload);
          toast.success('Booking draft created');
          console.log('[ShareQuotation] Draft upsert: create success');
          updated = true;
        }
      } catch (e: any) {
        // If checking for existing failed, attempt a blind create as fallback
        try {
          const amountPaise = Math.max(0, Math.round(Number(totalPrice || 0) * 100));
          const draftPayload = {
            productType: 'package',
            provider: 'CRM',
            title: title || 'Package Quotation',
            currency,
            amountPaise,
            startAt: travelStartDate || null,
            endAt: travelEndDate || null,
            providerRef,
            queryId: queryId!,
            customerId,
          } as any;
          console.log('[ShareQuotation] Draft upsert: fallback creating', draftPayload);
          await createBookingDraft(draftPayload);
          toast.success('Booking draft created');
          console.log('[ShareQuotation] Draft upsert: fallback create success');
          updated = true;
        } catch (e2: any) {
          toast.error(e2?.message || 'Failed to create booking draft');
        }
      }

      // 3) Upload/share the generated PDF once available (if any)
      try {
        const pdfBlob = await pdfPromise;
        if (pdfBlob) {
          const fd = new FormData();
          fd.append('type', 'PDF');
          fd.append('amount', String(Number(totalPrice || 0)));
          fd.append('notes', `Quotation shared for query #${queryId}`);
          // Include both conventions to maximize backend compatibility
          fd.append('query_id', String(queryId));
          fd.append('queryId', String(queryId));
          const file = new File([pdfBlob], `Quotation-${queryId}.pdf`, { type: 'application/pdf' });
          fd.append('file', file);
          console.log('[ShareQuotation] Uploading quotation PDF', { sizeBytes: (file as any)?.size });
          await uploadQuotation(fd);
          toast.success('Quotation PDF uploaded');
          console.log('[ShareQuotation] Upload success');
        }
      } catch (e: any) {
        toast.error(e?.message || 'Failed to upload quotation PDF');
        console.error('[ShareQuotation] Upload error', e);
      }

      if (updated) {
        // Overall success
      }
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <button type="button" onClick={handleShare} disabled={!canShare} className="package-editor-btn-primary">
      {isSharing ? 'Sharing…' : 'Share Quotation'}
    </button>
  );
}

export default CreatePackagePage;
